import { useEffect, useState } from "react";
import { api, formatErr } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import BabyAutoControl from "@/components/BabyAutoControl";
import { AutofillToggle } from "@/components/VaultControls";
import { Baby, Plus, Lock, Clock, AlertTriangle, X, Sparkles, Target, CalendarHeart } from "lucide-react";

export default function BabyFunds() {
  const [funds, setFunds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({
    is_expecting: false,
    child_name: "", child_dob: "", due_date: "",
    goal_amount: "", lock_mode: "until_18",
  });
  const [depositOpen, setDepositOpen] = useState(null);
  const [depositAmt, setDepositAmt] = useState("");
  const [convertOpen, setConvertOpen] = useState(null);
  const [convertForm, setConvertForm] = useState({ child_name: "", child_dob: "" });

  const load = async () => {
    try {
      const r = await api.get("/baby-funds");
      setFunds(r.data || []);
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.child_name && !form.is_expecting) {
      setError("Child name is required."); return;
    }
    if (!form.is_expecting && !form.child_dob) { setError("Date of birth is required."); return; }
    if (form.is_expecting && !form.due_date) { setError("Due date is required."); return; }

    setBusy(true); setError("");
    try {
      const body = {
        child_name: form.child_name || "Baby",
        is_expecting: form.is_expecting,
        lock_mode: form.lock_mode,
        ...(form.is_expecting
          ? { due_date: form.due_date }
          : { child_dob: form.child_dob }),
        ...(form.goal_amount ? { goal_amount: parseFloat(form.goal_amount) } : {}),
      };
      await api.post("/baby-funds", body);
      setShowCreate(false);
      setForm({
        is_expecting: false, child_name: "", child_dob: "", due_date: "",
        goal_amount: "", lock_mode: "until_18",
      });
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const deposit = async (fid) => {
    const amt = parseFloat(depositAmt);
    if (!amt || amt <= 0) { setError("Enter a positive amount"); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/baby-funds/${fid}/deposit`, { amount: amt });
      setDepositOpen(null); setDepositAmt("");
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const convert = async () => {
    if (!convertForm.child_dob) { setError("Date of birth is required."); return; }
    setBusy(true); setError("");
    try {
      await api.post(`/baby-funds/${convertOpen}/convert-to-born`, {
        child_dob: convertForm.child_dob,
        ...(convertForm.child_name ? { child_name: convertForm.child_name } : {}),
      });
      setConvertOpen(null); setConvertForm({ child_name: "", child_dob: "" });
      await load();
    } catch (e) {
      setError(formatErr(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const remove = async (fid) => {
    if (!window.confirm("Delete this baby fund? Only allowed if balance is $0.")) return;
    try { await api.delete(`/baby-funds/${fid}`); await load(); }
    catch (e) { setError(formatErr(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="min-h-screen bg-cream">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-6 lg:px-10 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage">Baby Funds</div>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mt-2">For when they turn 18.</h1>
            <p className="text-moss mt-3 max-w-2xl leading-relaxed">
              Open a long-horizon savings fund for a child you have — or one you&apos;re expecting.
              Set a savings goal and we&apos;ll track your progress.
            </p>
          </div>
          <button onClick={() => setShowCreate(true)}
            className="inline-flex items-center gap-2 bg-forest text-white rounded-xl px-5 py-3 text-sm font-medium hover:bg-forest-deep transition-colors"
            data-testid="baby-fund-create-btn">
            <Plus className="w-4 h-4" /> New baby fund
          </button>
        </div>

        {error && (
          <div className="mt-6 flex items-start gap-2 bg-clay-light border border-clay/30 text-ink rounded-lg px-4 py-3 text-sm" data-testid="baby-fund-error">
            <AlertTriangle className="w-4 h-4 text-clay shrink-0 mt-0.5" />
            <div>{error}</div>
          </div>
        )}

        {funds.length === 0 && (
          <div className="mt-10 bg-linen border border-sand rounded-2xl p-12 text-center" data-testid="baby-funds-empty">
            <Baby className="w-10 h-10 text-forest mx-auto" strokeWidth={1.5} />
            <h3 className="font-serif text-2xl mt-4">No baby funds yet</h3>
            <p className="text-sm text-moss mt-2">Open one for each child or expected baby.</p>
          </div>
        )}

        <div className="grid sm:grid-cols-2 gap-5 mt-8" data-testid="baby-funds-grid">
          {funds.map(f => {
            const today = new Date().toISOString().slice(0,10);
            const unlocked = f.target_date && f.target_date <= today;
            const expecting = f.is_expecting;
            const pct = f.goal_progress != null ? Math.round(f.goal_progress * 100) : null;
            return (
              <div key={f.id} className="bg-linen border border-sand rounded-2xl p-6"
                   data-testid={`baby-fund-${f.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-xs uppercase tracking-[0.2em] font-semibold text-sage flex items-center gap-1.5">
                      {expecting && <Sparkles className="w-3 h-3 text-gold" />}
                      {expecting ? "Expecting" : f.child_name}
                    </div>
                    <h3 className="font-serif text-2xl mt-1 truncate">{f.name}</h3>
                  </div>
                  {expecting
                    ? <CalendarHeart className="w-6 h-6 text-gold shrink-0" strokeWidth={1.5} />
                    : <Baby className="w-6 h-6 text-forest shrink-0" strokeWidth={1.5} />}
                </div>

                <div className="grid grid-cols-2 gap-3 mt-5">
                  <div className="bg-cream border border-sand rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Balance</div>
                    <div className="font-mono text-xl mt-1 text-ink">${f.balance.toFixed(2)}</div>
                  </div>
                  <div className="bg-cream border border-sand rounded-lg p-3">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
                      {expecting ? "Due" : "Unlocks"}
                    </div>
                    <div className="font-mono text-sm mt-1 text-ink">{expecting ? f.due_date : f.target_date}</div>
                  </div>
                </div>

                {f.goal_amount && (
                  <div className="mt-4" data-testid={`baby-fund-${f.id}-goal`}>
                    <div className="flex items-center justify-between text-[11px] mb-1.5">
                      <span className="text-sage flex items-center gap-1.5">
                        <Target className="w-3 h-3" /> Goal ${f.goal_amount.toFixed(2)}
                      </span>
                      <span className="font-mono text-forest font-semibold">{pct}%</span>
                    </div>
                    <div className="h-2 bg-sand rounded-full overflow-hidden">
                      <div className="h-full bg-forest transition-all"
                           style={{ width: `${pct}%` }} data-testid={`baby-fund-${f.id}-goal-bar`} />
                    </div>
                    {f.balance === 0 && (
                      <p className="text-[10px] text-sage mt-1.5 italic">Make your first deposit to start tracking.</p>
                    )}
                  </div>
                )}

                <div className="flex items-center gap-2 mt-4 text-xs text-moss">
                  {f.lock_mode === "until_18" ? (
                    <><Lock className="w-3.5 h-3.5" /> {unlocked ? "Unlocked" : "Locked until 18th birthday"}</>
                  ) : (
                    <><Clock className="w-3.5 h-3.5" /> 10-day cooling hold withdrawals</>
                  )}
                </div>

                {depositOpen === f.id ? (
                  <div className="mt-5 flex items-center gap-2">
                    <input type="number" placeholder="Amount" value={depositAmt}
                      onChange={e => setDepositAmt(e.target.value)}
                      className="flex-1 bg-cream border border-sand rounded-md px-3 py-2 text-sm font-mono"
                      data-testid={`baby-fund-deposit-amt-${f.id}`} />
                    <button onClick={() => deposit(f.id)} disabled={busy}
                      className="bg-forest text-white rounded-md px-4 py-2 text-xs font-medium hover:bg-forest-deep disabled:opacity-60"
                      data-testid={`baby-fund-deposit-confirm-${f.id}`}>
                      {busy ? "…" : "Add"}
                    </button>
                    <button onClick={() => { setDepositOpen(null); setDepositAmt(""); }}
                      className="text-sage hover:text-ink"><X className="w-4 h-4" /></button>
                  </div>
                ) : (
                  <div className="mt-5 flex items-center gap-2 flex-wrap">
                    <button onClick={() => setDepositOpen(f.id)}
                      className="flex-1 min-w-[120px] bg-forest text-white rounded-md py-2 text-xs font-medium hover:bg-forest-deep"
                      data-testid={`baby-fund-deposit-btn-${f.id}`}>Deposit</button>
                    {expecting && (
                      <button onClick={() => { setConvertOpen(f.id); setConvertForm({ child_name: f.child_name || "", child_dob: "" }); }}
                        className="text-xs px-3 py-2 rounded-md border border-gold text-gold hover:bg-gold hover:text-white"
                        data-testid={`baby-fund-convert-btn-${f.id}`}>
                        Baby is here →
                      </button>
                    )}
                    {f.balance === 0 && (
                      <button onClick={() => remove(f.id)}
                        className="text-xs text-clay hover:underline px-2"
                        data-testid={`baby-fund-delete-btn-${f.id}`}>Delete</button>
                    )}
                  </div>
                )}
                <BabyAutoControl fund={f} onChanged={load} onError={setError} />
                <div className="mt-3"><AutofillToggle vaultId={f.id} rule={f.autofill} onChanged={load} onMsg={setError} compact /></div>
              </div>
            );
          })}
        </div>

        {/* Create modal */}
        {showCreate && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-cream rounded-2xl border border-sand p-7 sm:p-8 max-w-md w-full max-h-[92vh] overflow-y-auto" data-testid="baby-fund-create-modal">
              <div className="flex items-start justify-between">
                <h3 className="font-serif text-2xl">Open a baby fund</h3>
                <button onClick={() => setShowCreate(false)} className="text-sage hover:text-ink" data-testid="baby-fund-create-close-btn"><X className="w-5 h-5" /></button>
              </div>

              {/* Expecting toggle */}
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Mode</div>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <button onClick={() => setForm({ ...form, is_expecting: false })}
                    data-testid="mode-born"
                    className={`text-xs py-2.5 px-3 rounded-lg border text-left ${
                      !form.is_expecting
                        ? "bg-forest text-white border-forest"
                        : "bg-white text-moss border-sand"
                    }`}>
                    <div className="font-medium flex items-center gap-1.5"><Baby className="w-3.5 h-3.5" /> Born</div>
                    <div className="text-[10px] opacity-80 mt-0.5">Your child is already here.</div>
                  </button>
                  <button onClick={() => setForm({ ...form, is_expecting: true })}
                    data-testid="mode-expecting"
                    className={`text-xs py-2.5 px-3 rounded-lg border text-left ${
                      form.is_expecting
                        ? "bg-gold text-white border-gold"
                        : "bg-white text-moss border-sand"
                    }`}>
                    <div className="font-medium flex items-center gap-1.5"><Sparkles className="w-3.5 h-3.5" /> Expecting</div>
                    <div className="text-[10px] opacity-80 mt-0.5">Save before they arrive.</div>
                  </button>
                </div>
              </div>

              <div className="mt-4 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">
                    {form.is_expecting ? "Name (optional — call it whatever you like)" : "Child name"}
                  </label>
                  <input value={form.child_name} onChange={e => setForm({ ...form, child_name: e.target.value })}
                    placeholder={form.is_expecting ? "Baby, Sprout, Acorn…" : "Olivia"} maxLength={80}
                    className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm"
                    data-testid="baby-fund-child-name-input" />
                </div>

                {form.is_expecting ? (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Expected due date</label>
                    <input type="date" value={form.due_date}
                      min={new Date().toISOString().slice(0,10)}
                      onChange={e => setForm({ ...form, due_date: e.target.value })}
                      className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm"
                      data-testid="baby-fund-due-date-input" />
                  </div>
                ) : (
                  <div>
                    <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Date of birth</label>
                    <input type="date" value={form.child_dob}
                      max={new Date().toISOString().slice(0,10)}
                      onChange={e => setForm({ ...form, child_dob: e.target.value })}
                      className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm"
                      data-testid="baby-fund-dob-input" />
                  </div>
                )}

                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage flex items-center gap-1.5">
                    <Target className="w-3 h-3" /> Savings goal (optional)
                  </label>
                  <div className="relative mt-1.5">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-sage">$</span>
                    <input type="number" min="0" step="100" value={form.goal_amount}
                      onChange={e => setForm({ ...form, goal_amount: e.target.value })}
                      placeholder="10000"
                      className="w-full bg-white border border-sand rounded-md pl-7 pr-3 py-2 text-sm font-mono"
                      data-testid="baby-fund-goal-input" />
                  </div>
                  <p className="text-[11px] text-sage mt-1.5">
                    {form.is_expecting
                      ? "How much do you want saved by the time they arrive? We'll track your progress."
                      : "How much do you want saved by their 18th birthday?"}
                  </p>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Lock mode</label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <button onClick={() => setForm({ ...form, lock_mode: "until_18" })}
                      data-testid="lock-mode-until-18"
                      className={`text-xs py-2 px-3 rounded-lg border text-left ${
                        form.lock_mode === "until_18"
                          ? "bg-forest text-white border-forest"
                          : "bg-white text-moss border-sand"
                      }`}>
                      <div className="font-medium">Locked till 18</div>
                      <div className="text-[10px] opacity-80 mt-0.5">Untouchable until target.</div>
                    </button>
                    <button onClick={() => setForm({ ...form, lock_mode: "cooling_hold" })}
                      data-testid="lock-mode-cooling-hold"
                      className={`text-xs py-2 px-3 rounded-lg border text-left ${
                        form.lock_mode === "cooling_hold"
                          ? "bg-forest text-white border-forest"
                          : "bg-white text-moss border-sand"
                      }`}>
                      <div className="font-medium">Cooling hold</div>
                      <div className="text-[10px] opacity-80 mt-0.5">10-day hold per withdrawal.</div>
                    </button>
                  </div>
                </div>
              </div>
              <button onClick={create} disabled={busy}
                className="w-full mt-6 bg-forest text-white rounded-xl py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-60"
                data-testid="baby-fund-create-confirm-btn">
                {busy ? "Creating…" : "Open fund"}
              </button>
            </div>
          </div>
        )}

        {/* Convert (expecting -> born) modal */}
        {convertOpen && (
          <div className="fixed inset-0 z-50 bg-ink/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-cream rounded-2xl border border-sand p-8 max-w-md w-full" data-testid="baby-fund-convert-modal">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gold flex items-center gap-1"><Sparkles className="w-3 h-3" /> Congratulations</div>
                  <h3 className="font-serif text-2xl mt-1">Baby is here!</h3>
                </div>
                <button onClick={() => setConvertOpen(null)} className="text-sage hover:text-ink" data-testid="baby-fund-convert-close-btn"><X className="w-5 h-5" /></button>
              </div>
              <p className="text-sm text-moss mt-3">Tell us the actual birth date — we&apos;ll re-anchor the unlock date to 18 years from this day.</p>
              <div className="mt-5 space-y-4">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Name</label>
                  <input value={convertForm.child_name}
                    onChange={e => setConvertForm({ ...convertForm, child_name: e.target.value })}
                    placeholder="Olivia" maxLength={80}
                    className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm"
                    data-testid="convert-child-name-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-sage">Date of birth</label>
                  <input type="date" value={convertForm.child_dob}
                    max={new Date().toISOString().slice(0,10)}
                    onChange={e => setConvertForm({ ...convertForm, child_dob: e.target.value })}
                    className="w-full mt-1.5 bg-white border border-sand rounded-md px-3 py-2 text-sm"
                    data-testid="convert-dob-input" />
                </div>
              </div>
              <button onClick={convert} disabled={busy}
                className="w-full mt-6 bg-forest text-white rounded-xl py-3 text-sm font-medium hover:bg-forest-deep disabled:opacity-60"
                data-testid="convert-confirm-btn">
                {busy ? "Updating…" : "Save birth date"}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
